/**
 * @file headlineLanguages.ts
 * @brief The login headline's language, picked from where the visitor is.
 *
 * @details
 * DES 03-10-2026: "Speak Urdu. / Be heard in theirs." with the language
 * taken from the visitor's region, and "One table. / Many tongues." when
 * there is no region to go on. Read on the device only: the browser's
 * languages, then its time zone. No IP lookup, so nothing leaves the page
 * and nothing costs anything. Order:
 * 1. the first browser language that is not English and that we list below;
 * 2. else the time zone's language (a Karachi laptop set to English still
 *    gets Urdu, which is the point);
 * 3. else English, when the browser's first language is English;
 * 4. else null: the fallback headline.
 * Only well-known languages from the app's catalog
 * (FRONTEND/src/shared/data/languages.ts) are here, with plain English names.
 *
 * DES, same day: "agar kisi mulk main aik say ziyda zubanin hoon to, language
 * flip hoti rahay". A time zone now lists its country's main languages, most
 * spoken first; the browser's own language leads when it is one of ours, and
 * AuthHeadline.tsx flips through the rest.
 * Nothing is stored on the device: the browser's languages and time zone are
 * read when the page draws, so no consent banner is needed (DES chose this
 * over remembering a signed-in user's language, which would need one).
 *
 * @author DES
 * @date 03-10-2026
 */

export const HEADLINE_LANGUAGES: Record<string, string> = {
  en: "English", ur: "Urdu", ar: "Arabic", hi: "Hindi", pa: "Punjabi", bn: "Bengali",
  fa: "Persian", ps: "Pashto", tr: "Turkish", fr: "French", es: "Spanish", de: "German",
  it: "Italian", pt: "Portuguese", nl: "Dutch", pl: "Polish", ro: "Romanian", el: "Greek",
  ru: "Russian", uk: "Ukrainian", sv: "Swedish", no: "Norwegian", da: "Danish", fi: "Finnish",
  cs: "Czech", hu: "Hungarian", bg: "Bulgarian", he: "Hebrew", zh: "Chinese", ja: "Japanese",
  ko: "Korean", id: "Indonesian", ms: "Malay", th: "Thai", vi: "Vietnamese", tl: "Filipino",
  ta: "Tamil", te: "Telugu", gu: "Gujarati", mr: "Marathi", sw: "Swahili", so: "Somali",
  am: "Amharic", sq: "Albanian", sd: "Sindhi", af: "Afrikaans", zu: "Zulu", yue: "Cantonese",
};

/**
 * Time zone → the languages most spoken there, most first. Countries people
 * move to (UK, US, the Gulf, Saudi Arabia, Germany, France, Canada,
 * Australia) list their largest communities' languages too, from census
 * figures, approximate and capped at 7 (DES 03-10-2026: "UK, USA, Europe,
 * Middle East jaisay mulkoon main loog kafi sari countries kay hotay hain").
 */
const TIMEZONE_LANGUAGES: Record<string, readonly string[]> = {
  "Asia/Karachi": ["ur", "pa", "ps", "sd"],
  "Asia/Riyadh": ["ar", "ur", "hi", "bn", "tl"],
  "Asia/Dubai": ["ar", "hi", "ur", "bn", "tl"],
  "Asia/Qatar": ["ar", "hi", "ur", "bn", "tl"],
  "Asia/Kuwait": ["ar", "hi", "ur", "bn", "tl"],
  "Asia/Bahrain": ["ar", "hi", "ur", "bn", "tl"],
  "Asia/Muscat": ["ar", "hi", "ur", "bn", "tl"],
  "Asia/Baghdad": ["ar"],
  "Asia/Amman": ["ar"],
  "Asia/Beirut": ["ar"],
  "Africa/Cairo": ["ar"],
  "Africa/Casablanca": ["ar", "fr"],
  "Africa/Algiers": ["ar", "fr"],
  "Africa/Tunis": ["ar", "fr"],
  "Asia/Kolkata": ["hi", "bn", "ta", "te", "mr", "gu", "pa"],
  "Asia/Calcutta": ["hi", "bn", "ta", "te", "mr", "gu", "pa"],
  "Asia/Dhaka": ["bn"],
  "Asia/Tehran": ["fa"],
  "Asia/Kabul": ["ps", "fa"],
  "Europe/Istanbul": ["tr"],
  "Europe/Paris": ["fr", "ar", "pt", "tr"],
  "Europe/Brussels": ["nl", "fr", "de"],
  "Europe/Madrid": ["es"],
  "America/Mexico_City": ["es"],
  "America/Bogota": ["es"],
  "America/Lima": ["es"],
  "America/Argentina/Buenos_Aires": ["es"],
  "America/Santiago": ["es"],
  "Europe/Berlin": ["de", "tr", "ru", "pl", "ar"],
  "Europe/Vienna": ["de"],
  "Europe/Zurich": ["de", "fr", "it"],
  "Europe/Rome": ["it"],
  "Europe/Lisbon": ["pt"],
  "America/Sao_Paulo": ["pt"],
  "Europe/Amsterdam": ["nl"],
  "Europe/Warsaw": ["pl"],
  "Europe/Bucharest": ["ro"],
  "Europe/Athens": ["el"],
  "Europe/Moscow": ["ru"],
  "Europe/Kyiv": ["uk"],
  "Europe/Kiev": ["uk"],
  "Europe/Stockholm": ["sv"],
  "Europe/Oslo": ["no"],
  "Europe/Copenhagen": ["da"],
  "Europe/Helsinki": ["fi", "sv"],
  "Europe/Prague": ["cs"],
  "Europe/Budapest": ["hu"],
  "Europe/Sofia": ["bg"],
  "Asia/Jerusalem": ["he", "ar"],
  "Asia/Shanghai": ["zh"],
  "Asia/Hong_Kong": ["yue", "zh", "en"],
  "Asia/Taipei": ["zh"],
  "Asia/Tokyo": ["ja"],
  "Asia/Seoul": ["ko"],
  "Asia/Jakarta": ["id"],
  "Asia/Kuala_Lumpur": ["ms", "zh", "ta"],
  "Asia/Bangkok": ["th"],
  "Asia/Ho_Chi_Minh": ["vi"],
  "Asia/Saigon": ["vi"],
  "Asia/Manila": ["tl", "en"],
  "Africa/Nairobi": ["sw", "en"],
  "Africa/Mogadishu": ["so"],
  "Africa/Addis_Ababa": ["am"],
  "Europe/Tirane": ["sq"],
  "Europe/London": ["en", "pl", "ro", "pa", "ur", "ar", "bn"],
  "Europe/Dublin": ["en"],
  "America/New_York": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Chicago": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Denver": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Los_Angeles": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Toronto": ["en", "fr", "zh", "pa", "es", "ar"],
  "Australia/Sydney": ["en", "zh", "ar", "vi", "pa"],
  "Australia/Melbourne": ["en", "zh", "ar", "vi", "pa"],
  "Pacific/Auckland": ["en"],
  "Asia/Singapore": ["en", "zh", "ms", "ta"],
  "Africa/Johannesburg": ["en", "af", "zu"],
  "America/Montreal": ["fr", "en"],
  "America/Phoenix": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Detroit": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Anchorage": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "Pacific/Honolulu": ["en", "es", "zh", "tl", "vi", "ar", "ko"],
  "America/Vancouver": ["en", "fr", "zh", "pa", "es", "ar"],
};

const base = (tag: string) => tag.toLowerCase().split(/[-_]/)[0];

/**
 * The languages to name in the headline, the likeliest first; empty for
 * "One table. Many tongues."
 */
export function headlineLanguages(input: {
  languages: readonly string[];
  timeZone?: string;
}): string[] {
  const bases = input.languages.map(base).filter(Boolean);
  const out: string[] = [];
  const add = (code: string | undefined) => {
    if (code && HEADLINE_LANGUAGES[code] && !out.includes(code)) out.push(code);
  };
  add(bases.find((b) => b !== "en" && HEADLINE_LANGUAGES[b]));
  for (const code of (input.timeZone && TIMEZONE_LANGUAGES[input.timeZone]) || []) add(code);
  if (out.length === 0 && bases[0] === "en") add("en");
  return out.map((code) => HEADLINE_LANGUAGES[code]);
}

/** The first of them, or null (kept for callers that want one). */
export function headlineLanguage(input: {
  languages: readonly string[];
  timeZone?: string;
}): string | null {
  return headlineLanguages(input)[0] ?? null;
}

/** The same, from this browser. Never throws. */
export function headlineLanguagesHere(): string[] {
  try {
    return headlineLanguages({
      languages: navigator.languages?.length ? navigator.languages : [navigator.language],
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch {
    return [];
  }
}
